// TutorialChooser — small modal listing the tours available for the active
// project, grouped: CORTEX tutorials (the product itself) on top, then the
// project's own tutorials in a separate section. Shown when the Tutorial
// launcher is pressed and more than one tour applies.
import React from "react";
import { GraduationCap, X, ChevronRight, CheckCircle2 } from "lucide-react";
import { isTutorialSeen } from "./TutorialFlow";

function TourButton({ t, onPick }) {
  return (
    <button
      key={t.key}
      data-tutorial-pick={t.key}
      onClick={() => onPick(t)}
      className="w-full text-left rounded-lg border border-page-divider hover:border-nav-active hover:bg-page-bg px-4 py-3 cursor-pointer bg-transparent transition-colors group"
    >
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-page-text">{t.title}</span>
        {isTutorialSeen(t.key) && (
          <span className="inline-flex items-center gap-1 text-[10px] text-green-600">
            <CheckCircle2 size={11} /> seen
          </span>
        )}
        <ChevronRight size={15} className="ml-auto text-page-muted group-hover:text-nav-active" />
      </div>
      <div className="text-xs text-page-muted mt-1">{t.description}</div>
      <div className="text-[10px] text-page-muted mt-1">{t.steps.length} steps</div>
    </button>
  );
}

export default function TutorialChooser({ groups, tours, onPick, onClose }) {
  // `groups` = [{ key, label, tours }] (cortex first). A flat `tours` prop is
  // still accepted for backward compatibility (rendered as one unlabeled group).
  const sections = groups?.length ? groups : [{ key: "all", label: null, tours: tours || [] }];
  return (
    <div className="fixed inset-0 flex items-center justify-center" style={{ zIndex: 100000 }}>
      <div className="absolute inset-0" style={{ background: "rgba(9,14,34,.55)" }} onClick={onClose} />
      <div className="relative w-[460px] max-w-[calc(100vw-24px)] rounded-[10px] bg-card-bg text-page-text shadow-2xl border border-page-divider overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 bg-nav-active text-white">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <GraduationCap size={16} /> Tutorials
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/20 border-0 bg-transparent text-white cursor-pointer" title="Close">
            <X size={16} />
          </button>
        </div>
        <div className="p-3 space-y-3 max-h-[60vh] overflow-y-auto">
          {sections.map((g) => (
            <div key={g.key} data-tutorial-group={g.key}>
              {g.label && (
                <div className="px-1 pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-page-muted">
                  {g.label}
                </div>
              )}
              <div className="space-y-2">
                {g.tours.map((t) => <TourButton key={t.key} t={t} onPick={onPick} />)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
